import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Timer, Link2, Users, Dices, Radio } from 'lucide-react';
import { displayTimeAPI } from '../services/api';
import type { DtLive } from '../types';
import KpiCard from './KpiCard';

// LIVE ad display time (2026-10-02). Reads the production table ad_display_time via
// GET /api/admin/display-time/live: per-exposure on-screen time on all four platforms,
// linked to the ad record (advertiser / category) the existing pipeline captured.
// The internal test line is excluded unless the checkbox is ticked.

const PLATFORM_LABEL: Record<string, string> = {
  youtube: 'YouTube', instagram: 'Instagram', x_twitter: 'X', facebook: 'Facebook',
};
const jst = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo', hour12: false }) : '—';
const sec = (ms: number | null) => (ms === null ? '—' : (ms / 1000).toFixed(1));

export default function DisplayTimeLive() {
  const { t } = useTranslation();
  const [days, setDays] = useState(7);
  const [platform, setPlatform] = useState('');
  const [includeTest, setIncludeTest] = useState(false);
  const [gamblingOnly, setGamblingOnly] = useState(false);
  const [data, setData] = useState<DtLive | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true); setError(null);
    displayTimeAPI.getLive({ days, platform: platform || undefined, includeTest, gamblingOnly })
      .then(setData).catch(e => setError(String(e))).finally(() => setLoading(false));
  }, [days, platform, includeTest, gamblingOnly]);

  const u = t('displayTime.sec', '秒');
  const c = data?.capture;
  const total = data?.platforms.reduce((a, p) => a + p.exposures, 0) ?? 0;
  const linked = data?.platforms.reduce((a, p) => a + p.linked, 0) ?? 0;
  const gambling = data?.platforms.reduce((a, p) => a + p.gambling, 0) ?? 0;
  const maxMs = Math.max(1, ...(data?.exposures.map(e => e.onScreenMs) ?? [1]));

  return (
    <div>
      {c && (
        <div className={`rounded-lg border-l-4 p-4 mb-5 text-sm flex gap-3 ${c.disabled
          ? 'border-red-500 bg-red-50 dark:bg-red-900/20 text-red-900 dark:text-red-200'
          : 'border-teal-500 bg-teal-50 dark:bg-teal-900/20 text-teal-900 dark:text-teal-100'}`}>
          <Radio className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <b>{c.disabled ? t('displayTime.liveOff', '表示時間の取得は停止中です（緊急停止フラグ）。')
              : t('displayTime.liveOn', '本番で取得中')}</b>{' '}
            {!c.disabled && (c.allUsers ? t('displayTime.liveAll', '— 全ユーザー') : t('displayTime.liveShadow', '— 検証用回線のみ'))}
            <span className="ml-2 text-xs opacity-80">
              {t('displayTime.liveRelease', 'リリース {{r}}（起動 {{s}}）', { r: c.release ?? '—', s: jst(c.startedAt) })}
            </span>
          </div>
        </div>
      )}

      {data?.output && (
        <div className={`rounded-lg border-l-4 p-3 mb-5 text-sm ${data.output.on
          ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20 text-teal-900 dark:text-teal-100'
          : 'border-gray-400 bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-200'}`}>
          <b>{t('displayTime.outputTitle', '顧客向け出力（DISPLAY_TIME_OUTPUT）')}:</b>{' '}
          {data.output.on
            ? t('displayTime.outputOn', 'ON — 納品データ等に表示（対象媒体: {{p}}）', { p: data.output.platforms.join(', ') })
            : t('displayTime.outputOff', 'OFF — 計測は継続し DB に保存。納品データ・顧客画面には出しません。')}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-5 text-sm text-gray-700 dark:text-gray-300">
        <label className="flex items-center gap-2">{t('displayTime.filterDays', '期間')}
          <select className="rounded border px-2 py-1 bg-white dark:bg-[#231d17] dark:border-gray-700"
            value={days} onChange={e => setDays(Number(e.target.value))}>
            <option value={1}>{t('displayTime.days1', '直近1日')}</option>
            <option value={7}>{t('displayTime.days7', '直近7日')}</option>
            <option value={30}>{t('displayTime.days30', '直近30日')}</option>
            <option value={400}>{t('displayTime.daysAll', '全期間（7月の YouTube を含む・約10秒）')}</option>
          </select>
        </label>
        <label className="flex items-center gap-2">{t('displayTime.filterPlatform', '媒体')}
          <select className="rounded border px-2 py-1 bg-white dark:bg-[#231d17] dark:border-gray-700"
            value={platform} onChange={e => setPlatform(e.target.value)}>
            <option value="">{t('displayTime.allPlatforms', 'すべて')}</option>
            {Object.entries(PLATFORM_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={gamblingOnly} onChange={e => setGamblingOnly(e.target.checked)} />
          {t('displayTime.gamblingOnly', 'ギャンブル広告のみ')}
        </label>
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={includeTest} onChange={e => setIncludeTest(e.target.checked)} />
          {t('displayTime.includeTest', '検証用回線（B333）を含める')}
        </label>
        {loading && <span className="text-gray-400">{t('common.loading', '読み込み中…')}</span>}
      </div>

      {error && <p className="text-red-500 mb-4">{error}</p>}

      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <KpiCard icon={Timer} color="text-teal-500" label={t('displayTime.kpiExposures', '表示時間を取得できた接触')}
              value={`${total}`} sub={t('displayTime.kpiExposuresSub', '1接触＝同じ広告・同じ人・同じ1時間')} />
            <KpiCard icon={Link2} color="text-indigo-500" label={t('displayTime.kpiLinked', '広告データと紐付け')}
              value={`${linked} / ${total}`} sub={t('displayTime.kpiLinkedSub', '広告主・分類を参照できる接触')} />
            <KpiCard icon={Dices} color="text-rose-500" label={t('displayTime.kpiGambling', 'うちギャンブル広告')}
              value={`${gambling}`} sub={t('displayTime.kpiGamblingSub', '分類が gambling_* の接触')} />
            <KpiCard icon={Users} color="text-sky-500" label={t('displayTime.kpiMembers', '対象者数')}
              value={`${data.platforms.reduce((a, p) => Math.max(a, p.members), 0)}`}
              sub={t('displayTime.kpiMembersSub', '媒体別の最大値')} />
          </div>

          <section className="bg-white dark:bg-[#231d17] rounded-xl shadow-sm p-5 mb-6">
            <h2 className="font-semibold text-gray-900 dark:text-white mb-3">{t('displayTime.byPlatform', '媒体別')}</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
                  <th className="py-2 pr-3">{t('displayTime.colPlatform', '媒体')}</th>
                  <th className="py-2 pr-3 text-right">{t('displayTime.colExposures', '接触')}</th>
                  <th className="py-2 pr-3 text-right">{t('displayTime.colLinked', '紐付け')}</th>
                  <th className="py-2 pr-3 text-right">{t('displayTime.colGambling', 'ギャンブル')}</th>
                  <th className="py-2 pr-3 text-right">{t('displayTime.colMedian', '表示時間 中央値')}</th>
                  <th className="py-2 pr-3 text-right">{t('displayTime.colIqr', '25%〜75%')}</th>
                </tr></thead>
                <tbody>
                  {data.platforms.map(p => (
                    <tr key={p.platform} className="border-b dark:border-gray-800 text-gray-800 dark:text-gray-200">
                      <td className="py-2 pr-3 font-medium">{PLATFORM_LABEL[p.platform]}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{p.exposures}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{p.linked}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{p.gambling}</td>
                      <td className="py-2 pr-3 text-right tabular-nums font-semibold">{sec(p.medianMs)} {u}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{sec(p.p25Ms)} – {sec(p.p75Ms)} {u}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="bg-white dark:bg-[#231d17] rounded-xl shadow-sm p-5 mb-6">
            <h2 className="font-semibold text-gray-900 dark:text-white mb-3">{t('displayTime.daily', '日別の接触数（JST）')}</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
                  <th className="py-2 pr-3">{t('displayTime.colDay', '日付')}</th>
                  {Object.values(PLATFORM_LABEL).map(v => <th key={v} className="py-2 pr-3 text-right">{v}</th>)}
                </tr></thead>
                <tbody>
                  {data.daily.slice(0, 31).map(d => (
                    <tr key={d.day} className="border-b dark:border-gray-800 text-gray-800 dark:text-gray-200">
                      <td className="py-1.5 pr-3 tabular-nums">{d.day}</td>
                      {Object.keys(PLATFORM_LABEL).map(k => (
                        <td key={k} className="py-1.5 pr-3 text-right tabular-nums">{(d[k] as number) ?? 0}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="bg-white dark:bg-[#231d17] rounded-xl shadow-sm p-5 mb-6">
            <h2 className="font-semibold text-gray-900 dark:text-white mb-1">
              {t('displayTime.exposures', '接触ごとの表示時間（新しい順・最大300件）')}
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              {t('displayTime.exposuresNote', '全 {{n}} 件。X・Facebook は画面に出ていた時間の合計、YouTube・Instagram は最大値（送信のたびに値が伸びるため）。',
                { n: data.totalExposures })}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
                  <th className="py-2 pr-3">{t('displayTime.colTime', '時刻（JST）')}</th>
                  <th className="py-2 pr-3">{t('displayTime.colPlatform', '媒体')}</th>
                  <th className="py-2 pr-3">{t('displayTime.colCode', 'コード')}</th>
                  <th className="py-2 pr-3">{t('displayTime.colAdvertiser', '広告主')}</th>
                  <th className="py-2 pr-3">{t('displayTime.colCategory', '分類')}</th>
                  <th className="py-2 pr-3">{t('displayTime.colOnScreen', '画面表示時間')}</th>
                </tr></thead>
                <tbody>
                  {data.exposures.map((e, i) => (
                    <tr key={i} className="border-b dark:border-gray-800 text-gray-800 dark:text-gray-200">
                      <td className="py-1.5 pr-3 tabular-nums whitespace-nowrap">{jst(e.firstSeen)}</td>
                      <td className="py-1.5 pr-3">{PLATFORM_LABEL[e.platform] ?? e.platform}</td>
                      <td className="py-1.5 pr-3 tabular-nums">{e.code ?? '—'}</td>
                      <td className="py-1.5 pr-3">{e.advertiser ?? <span className="text-amber-600">
                        {e.linked ? t('displayTime.noAdvertiser', '（広告主なし）') : t('displayTime.unlinked', '未紐付け')}</span>}</td>
                      <td className={`py-1.5 pr-3 ${String(e.category ?? '').startsWith('gambling_') ? 'text-rose-600 font-medium' : ''}`}>
                        {e.category ?? '—'}</td>
                      <td className="py-1.5 pr-3">
                        <div className="flex items-center gap-2">
                          <div className="h-2.5 rounded bg-teal-500" style={{ width: `${Math.max(2, 140 * e.onScreenMs / maxMs)}px` }} />
                          <span className="tabular-nums">{sec(e.onScreenMs)} {u}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
